/**
 * RhNotesFraisPanel — écrans 1 (saisie) & 2 (mes notes) du mockup notes de frais.
 * Onglet RH accessible à tout le personnel + élus. Reproduit le mockup à l'identique.
 */
import { useState, useEffect } from 'react';
import { Card, Button, Alert, Badge } from '@/components/ui';
import { api, ApiException } from '@/lib/api';
import { compressImageIfNeeded } from '@/lib/image-compress';
import { Plus, Trash2, Paperclip } from 'lucide-react';
import type { NdfNote, NdfTypeFrais, RhOrdreMission } from '@roronoa-collectivites/shared-types';

const eur = (n: number) => `${n.toFixed(2).replace('.', ',')} €`;
const PUISSANCES = [3, 4, 5, 6, 7];

const STATUT_BADGE: Record<string, { label: string; v: 'success' | 'urgent' | 'info' | 'neutral' | 'rh' }> = {
  brouillon: { label: 'Brouillon', v: 'neutral' },
  attente_n1: { label: 'En attente N1', v: 'urgent' },
  attente_compta: { label: 'En attente compta', v: 'info' },
  validee: { label: 'Validée', v: 'info' },
  remboursee: { label: 'Remboursé', v: 'success' },
  refusee: { label: 'Refusée', v: 'urgent' },
};
const ETAPE: Record<string, string> = { brouillon: '—', attente_n1: 'Manager', attente_compta: 'Comptabilité', validee: 'Comptabilité', remboursee: '—', refusee: '—' };

interface LigneDraft {
  key: string; typeFraisId: string; dateDepense: string; libelle: string;
  montantTtc: string; montantTva: string;
  distanceKm: string; puissanceFiscale: number;
  indemnite: number | null;
  /** Taux €/km appliqué par l'estimation serveur — affiché, jamais renvoyé. */
  tauxEstime: number | null;
  lieuDepart: string; lieuArrivee: string; ordreMissionId: string;
  /** Libellé porté par la note — sert d'option de secours si l'ordre a été désactivé depuis. */
  ordreMissionLibelle: string | null;
  justificatifR2Key: string | null; justificatifNom: string | null; compressInfo: string | null;
}

function ligneVide(types: NdfTypeFrais[]): LigneDraft {
  const t = types[0];
  return {
    key: crypto.randomUUID(), typeFraisId: t?.id ?? '', dateDepense: new Date().toISOString().slice(0, 10), libelle: '',
    montantTtc: '', montantTva: '', distanceKm: '', puissanceFiscale: 5, indemnite: null, tauxEstime: null,
    lieuDepart: '', lieuArrivee: '', ordreMissionId: '', ordreMissionLibelle: null,
    justificatifR2Key: null, justificatifNom: null, compressInfo: null,
  };
}

export function RhNotesFraisPanel() {
  const [mode, setMode] = useState<'liste' | 'saisie'>('liste');
  const [notes, setNotes] = useState<NdfNote[]>([]);
  const [types, setTypes] = useState<NdfTypeFrais[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [editId, setEditId] = useState<string | null>(null);

  async function charger() {
    try {
      const [n, t] = await Promise.all([api.notesFrais.notes.mine(), api.notesFrais.types.list(true)]);
      setNotes(n); setTypes(t);
    } catch (e) { setError(e instanceof ApiException ? e.message : 'Erreur'); }
  }
  useEffect(() => { void charger(); }, []);

  function nouvelle() { setEditId(null); setMode('saisie'); }
  async function editer(id: string) {
    setEditId(id); setMode('saisie');
  }

  if (mode === 'saisie') {
    return <SaisieNote types={types} editId={editId} onDone={() => { setMode('liste'); void charger(); }} onCancel={() => setMode('liste')} />;
  }

  // Stats (écran 2)
  const enCours = notes.filter((n) => ['attente_n1', 'attente_compta', 'validee'].includes(n.statut)).length;
  const rembourse = notes.filter((n) => n.statut === 'remboursee').reduce((s, n) => s + n.montantRetenu, 0);
  const brouillons = notes.filter((n) => n.statut === 'brouillon').length;
  const totalDeclare = notes.reduce((s, n) => s + n.montantTotal, 0);

  return (
    <div className="space-y-4">
      {error && <Alert variant="error">{error}</Alert>}

      <div className="grid gap-2.5" style={{ gridTemplateColumns: 'repeat(auto-fill,minmax(150px,1fr))' }}>
        <StatBox label="En cours" value={String(enCours)} />
        <StatBox label="Remboursé (année)" value={eur(rembourse)} tone="ok" />
        <StatBox label="À compléter" value={String(brouillons)} tone={brouillons > 0 ? 'warn' : undefined} />
        <StatBox label="Total déclaré" value={eur(totalDeclare)} />
      </div>

      <Card className="overflow-hidden p-0">
        <div className="flex items-center gap-2.5 border-b border-border px-4 py-3">
          <h3 className="text-sm font-semibold text-ink">Historique</h3>
          <Button variant="rh" size="sm" className="ml-auto" onClick={nouvelle}><Plus className="h-4 w-4" /> Nouvelle note</Button>
        </div>
        {notes.length === 0 ? <p className="px-4 py-6 text-center text-sm text-ink-subtle">Aucune note de frais.</p> : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-border bg-surface/40 text-left text-[10px] uppercase tracking-wider text-ink-subtle">
                  <th className="px-4 py-2.5 font-medium">Note</th>
                  <th className="px-4 py-2.5 font-medium">Date</th>
                  <th className="px-4 py-2.5 text-right font-medium">Montant</th>
                  <th className="px-4 py-2.5 font-medium">Étape</th>
                  <th className="px-4 py-2.5 font-medium">Statut</th>
                  <th className="px-4 py-2.5 text-right font-medium">Action</th>
                </tr>
              </thead>
              <tbody>
                {notes.map((n) => (
                  <tr key={n.id} className="border-b border-border/60 last:border-0">
                    <td className="px-4 py-2.5 text-ink">{n.titre}{n.deplacementObjet && <span className="ml-2 rounded-r-pill bg-rh/10 px-2 py-0.5 text-2xs text-ink-muted">Déplacement : {n.deplacementObjet}</span>}</td>
                    <td className="px-4 py-2.5 text-ink-muted">{new Date(`${n.dateNote}T00:00:00`).toLocaleDateString('fr-FR', { day: 'numeric', month: 'short' })}</td>
                    <td className="px-4 py-2.5 text-right tabular-nums text-ink">{eur(n.montantTotal)}</td>
                    <td className="px-4 py-2.5 text-ink-subtle">{ETAPE[n.statut] ?? '—'}</td>
                    <td className="px-4 py-2.5"><Badge variant={STATUT_BADGE[n.statut]?.v ?? 'neutral'}>{STATUT_BADGE[n.statut]?.label ?? n.statut}</Badge></td>
                    <td className="px-4 py-2.5 text-right">
                      {n.statut === 'brouillon'
                        ? <><Button variant="ghost" size="sm" onClick={() => void editer(n.id)}>Modifier</Button> <button type="button" onClick={() => void supprimer(n.id, charger, setError)} className="text-xs text-ink-subtle hover:text-danger"><Trash2 className="inline h-3.5 w-3.5" /></button></>
                        : <Button variant="ghost" size="sm" onClick={() => void editer(n.id)}>Voir</Button>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>
    </div>
  );
}

async function supprimer(id: string, reload: () => Promise<void>, setError: (s: string | null) => void) {
  try { await api.notesFrais.notes.remove(id); await reload(); } catch (e) { setError(e instanceof ApiException ? e.message : 'Erreur'); }
}

function StatBox({ label, value, tone }: { label: string; value: string; tone?: 'ok' | 'warn' }) {
  const c = tone === 'ok' ? 'text-success' : tone === 'warn' ? 'text-warn' : 'text-ink';
  return <div className="rounded-r-lg border border-border bg-surface px-3.5 py-3"><div className="text-[11px] text-ink-subtle">{label}</div><div className={`mt-0.5 text-2xl font-bold ${c}`}>{value}</div></div>;
}

// ── Écran 1 : saisie ────────────────────────────────────────────────────────
function SaisieNote({ types, editId, onDone, onCancel }: { types: NdfTypeFrais[]; editId: string | null; onDone: () => void; onCancel: () => void }) {
  const [titre, setTitre] = useState('');
  const [dateNote, setDateNote] = useState(new Date().toISOString().slice(0, 10));
  const [lignes, setLignes] = useState<LigneDraft[]>([ligneVide(types)]);
  const [ordres, setOrdres] = useState<RhOrdreMission[]>([]);
  const [lectureSeule, setLectureSeule] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    // Référentiel commun (item 5.18) : s'il est vide, le select ne s'affiche
    // pas — inutile de montrer une liste sans choix.
    api.notesFrais.ordresMission().then(setOrdres).catch(() => {});
  }, []);

  useEffect(() => {
    if (!editId) return;
    api.notesFrais.notes.get(editId).then((n) => {
      setTitre(n.titre); setDateNote(n.dateNote);
      setLectureSeule(n.statut !== 'brouillon');
      setLignes((n.lignes ?? []).map((l) => ({
        key: l.id, typeFraisId: l.typeFraisId ?? (types[0]?.id ?? ''), dateDepense: l.dateDepense, libelle: l.libelle ?? '',
        montantTtc: l.montantTtc ? String(l.montantTtc) : '', montantTva: l.montantTva != null ? String(l.montantTva) : '',
        distanceKm: l.distanceKm != null ? String(l.distanceKm) : '', puissanceFiscale: l.puissanceFiscale ?? 5,
        indemnite: l.tauxKm != null && l.distanceKm != null ? l.montantTtc : null,
        tauxEstime: l.tauxKm ?? null,
        lieuDepart: l.lieuDepart ?? '', lieuArrivee: l.lieuArrivee ?? '', ordreMissionId: l.ordreMissionId ?? '',
        ordreMissionLibelle: l.ordreMissionLibelle ?? null,
        justificatifR2Key: null, justificatifNom: l.justificatifNom, compressInfo: null,
      })));
    }).catch(() => {});
  }, [editId]);

  function typeDe(id: string) { return types.find((t) => t.id === id); }
  function setLigne(key: string, patch: Partial<LigneDraft>) { setLignes((ls) => ls.map((l) => l.key === key ? { ...l, ...patch } : l)); }
  function ajouterLigne() { setLignes((ls) => [...ls, ligneVide(types)]); }
  function retirerLigne(key: string) { setLignes((ls) => ls.filter((l) => l.key !== key)); }

  async function calculerKm(l: LigneDraft) {
    const d = Number(l.distanceKm);
    if (!d || !l.puissanceFiscale) return;
    try {
      // L'année de la DÉPENSE, pas celle du jour : une note de décembre saisie
      // en janvier doit se situer dans le barème de décembre.
      const annee = Number((l.dateDepense || dateNote || '').slice(0, 4)) || undefined;
      const r = await api.notesFrais.calculKm(d, l.puissanceFiscale, annee);
      setLigne(l.key, { indemnite: r.montant, tauxEstime: r.taux, montantTtc: String(r.montant) });
    } catch { /* l'aide à la saisie est un confort : le serveur recalcule */ }
  }

  async function choisirFichier(key: string, file: File) {
    try {
      const orig = file.size;
      const compressed = await compressImageIfNeeded(file);
      const up = await api.notesFrais.uploadJustificatif(compressed);
      const info = compressed.size < orig ? `${Math.round(compressed.size / 1024)} Ko (compressé depuis ${(orig / 1024 / 1024).toFixed(1)} Mo)` : `${Math.round(compressed.size / 1024)} Ko`;
      setLigne(key, { justificatifR2Key: up.r2Key, justificatifNom: up.nom, compressInfo: info });
    } catch (e) { setError(e instanceof ApiException ? e.message : 'Upload impossible'); }
  }

  const total = lignes.reduce((s, l) => s + (Number(l.montantTtc) || 0), 0);

  function payload(envoyer: boolean) {
    return {
      titre: titre.trim() || 'Note de frais', dateNote,
      envoyer,
      lignes: lignes.map((l) => {
        const t = typeDe(l.typeFraisId);
        // Item 5.18 — lieux et ordre de mission accompagnent toute ligne.
        const commun = {
          lieuDepart: l.lieuDepart.trim() || null,
          lieuArrivee: l.lieuArrivee.trim() || null,
          ordreMissionId: l.ordreMissionId || null,
        };
        if (t?.baremeKm) {
          return { typeFraisId: l.typeFraisId, dateDepense: l.dateDepense, libelle: l.libelle,
            montantTtc: Number(l.montantTtc) || 0, distanceKm: Number(l.distanceKm) || null,
            // On n'envoie plus de taux : il était redérivé à l'envers
            // (montant / distance) et le serveur le recalcule depuis le barème.
            puissanceFiscale: l.puissanceFiscale, ...commun,
            justificatifR2Key: l.justificatifR2Key, justificatifNom: l.justificatifNom };
        }
        return { typeFraisId: l.typeFraisId, dateDepense: l.dateDepense, libelle: l.libelle,
          montantTtc: Number(l.montantTtc) || 0, montantTva: l.montantTva ? Number(l.montantTva.replace(',', '.')) : null,
          ...commun,
          justificatifR2Key: l.justificatifR2Key, justificatifNom: l.justificatifNom };
      }),
    };
  }

  async function enregistrer(envoyer: boolean) {
    setSaving(true); setError(null);
    try {
      if (editId) { await api.notesFrais.notes.update(editId, payload(false)); if (envoyer) await api.notesFrais.notes.soumettre(editId); }
      else { const r = await api.notesFrais.notes.create(payload(envoyer)); void r; }
      onDone();
    } catch (e) { setError(e instanceof ApiException ? e.message : 'Erreur'); }
    finally { setSaving(false); }
  }

  const inp = 'h-10 w-full rounded-r-md border border-border bg-surface/60 px-2.5 text-sm text-ink disabled:opacity-60';

  return (
    <div className="space-y-4">
      {error && <Alert variant="error">{error}</Alert>}
      <button type="button" onClick={onCancel} className="text-xs text-ink-subtle hover:text-ink">← Retour à mes notes</button>

      <Card className="p-5">
        <div className="grid grid-cols-2 gap-3">
          <div><div className="mb-1 text-xs text-ink-subtle">Titre de la note</div><input type="text" disabled={lectureSeule} value={titre} onChange={(e) => setTitre(e.target.value)} className={inp} /></div>
          <div><div className="mb-1 text-xs text-ink-subtle">Date</div><input type="date" disabled={lectureSeule} value={dateNote} onChange={(e) => setDateNote(e.target.value)} className={inp} /></div>
        </div>

        <h3 className="mb-2 mt-4 text-sm font-semibold text-ink">Dépenses</h3>

        {lignes.map((l) => {
          const t = typeDe(l.typeFraisId);
          const depasse = t?.plafond != null && Number(l.montantTtc) > t.plafond;
          return (
            <div key={l.key} className="mb-2.5 rounded-r-lg border border-border bg-surface/40 p-3.5">
              <div className="mb-2.5 flex items-center justify-between">
                {t?.baremeKm ? <Badge variant="rh">Voiture perso · barème km</Badge> : <Badge variant="neutral">{t?.libelle ?? 'Dépense'}</Badge>}
                {!lectureSeule && lignes.length > 1 && <button type="button" onClick={() => retirerLigne(l.key)} className="text-xs text-ink-subtle hover:text-danger"><Trash2 className="mr-1 inline h-3.5 w-3.5" />Retirer</button>}
              </div>
              <div className="grid grid-cols-3 gap-2.5">
                <div><div className="mb-1 text-xs text-ink-subtle">Type de frais</div>
                  <select disabled={lectureSeule} value={l.typeFraisId} onChange={(e) => setLigne(l.key, { typeFraisId: e.target.value, indemnite: null })} className={inp}>
                    {types.map((tt) => <option key={tt.id} value={tt.id}>{tt.libelle}</option>)}
                  </select>
                </div>
                <div><div className="mb-1 text-xs text-ink-subtle">Date de la dépense</div><input type="date" disabled={lectureSeule} value={l.dateDepense} onChange={(e) => setLigne(l.key, { dateDepense: e.target.value })} className={inp} /></div>
                <div><div className="mb-1 text-xs text-ink-subtle">Libellé</div><input type="text" disabled={lectureSeule} value={l.libelle} onChange={(e) => setLigne(l.key, { libelle: e.target.value })} className={inp} /></div>
              </div>

              {t?.baremeKm ? (
                <>
                  <div className="mt-2.5 grid grid-cols-2 gap-2.5">
                    <div><div className="mb-1 text-xs text-ink-subtle">Lieu de départ</div><input type="text" disabled={lectureSeule} value={l.lieuDepart} onChange={(e) => setLigne(l.key, { lieuDepart: e.target.value })} placeholder="Mairie" className={inp} /></div>
                    <div><div className="mb-1 text-xs text-ink-subtle">Lieu d'arrivée</div><input type="text" disabled={lectureSeule} value={l.lieuArrivee} onChange={(e) => setLigne(l.key, { lieuArrivee: e.target.value })} placeholder="Préfecture de La Rochelle" className={inp} /></div>
                  </div>
                  <div className="mt-2.5 grid grid-cols-3 gap-2.5">
                    <div><div className="mb-1 text-xs text-ink-subtle">Distance (km)</div><input type="text" disabled={lectureSeule} value={l.distanceKm} onChange={(e) => setLigne(l.key, { distanceKm: e.target.value })} onBlur={() => void calculerKm(l)} className={inp} /></div>
                    <div><div className="mb-1 text-xs text-ink-subtle">Puissance fiscale</div>
                      <select disabled={lectureSeule} value={l.puissanceFiscale} onChange={(e) => { setLigne(l.key, { puissanceFiscale: Number(e.target.value) }); }} onBlur={() => void calculerKm(l)} className={inp}>
                        {PUISSANCES.map((p) => <option key={p} value={p}>{p >= 7 ? '7 CV et plus' : `${p} CV`}</option>)}
                      </select>
                    </div>
                    <div><div className="mb-1 text-xs text-ink-subtle">Indemnité calculée</div><input type="text" readOnly value={l.montantTtc ? eur(Number(l.montantTtc)) : '—'} className={`${inp} font-semibold`} style={{ color: 'oklch(var(--c-success))' }} />
                      {/* Le DÉTAIL de l'estimation : distance × taux de la tranche où se
                          situe le cumul annuel du déclarant. Le taux vient du serveur
                          (/calcul-km) — jamais recalculé ici, une seule vérité. */}
                      {l.tauxEstime != null && l.distanceKm && (
                        <p className="mt-1 text-[11px] text-ink-subtle">
                          {l.distanceKm} km × {String(l.tauxEstime).replace('.', ',')} €/km
                          — estimation, le montant définitif est recalculé à l'enregistrement.
                        </p>
                      )}
                    </div>
                  </div>
                </>
              ) : (
                <div className="mt-2.5 grid grid-cols-3 gap-2.5">
                  <div><div className="mb-1 text-xs text-ink-subtle">Montant TTC</div><input type="text" disabled={lectureSeule} value={l.montantTtc} onChange={(e) => setLigne(l.key, { montantTtc: e.target.value })} className={inp} style={depasse ? { borderColor: 'oklch(var(--c-warn))' } : undefined} /></div>
                  <div><div className="mb-1 text-xs text-ink-subtle">Dont TVA (facultatif)</div><input type="text" disabled={lectureSeule} value={l.montantTva} onChange={(e) => setLigne(l.key, { montantTva: e.target.value })} className={inp} /></div>
                  <div><div className="mb-1 text-xs text-ink-subtle">Plafond du type</div><input type="text" readOnly value={t?.plafond != null ? eur(t.plafond) : '—'} className={inp} style={depasse ? { color: 'oklch(var(--c-warn))' } : { color: 'var(--ink-subtle)' }} /></div>
                </div>
              )}

              {/* Ordre de mission (item 5.18) : sur TOUTE ligne — un repas ou une
                  nuitée se rattachent à une mission autant qu'un trajet. Masqué si
                  la commune n'a rien défini dans les référentiels RH. */}
              {(ordres.length > 0 || l.ordreMissionId) && (
                <div className="mt-2.5">
                  <div className="mb-1 text-xs text-ink-subtle">Ordre de mission (facultatif)</div>
                  <select disabled={lectureSeule} value={l.ordreMissionId} onChange={(e) => setLigne(l.key, { ordreMissionId: e.target.value })} className={inp}>
                    <option value="">—</option>
                    {/* Ordre désactivé depuis la saisie : on l'affiche quand même,
                        sinon le select retomberait silencieusement sur « — ». */}
                    {l.ordreMissionId && !ordres.some((o) => o.id === l.ordreMissionId) && (
                      <option value={l.ordreMissionId}>{l.ordreMissionLibelle ?? 'Ordre désactivé'}</option>
                    )}
                    {ordres.map((o) => <option key={o.id} value={o.id}>{o.libelle}</option>)}
                  </select>
                </div>
              )}

              {!t?.baremeKm && (
                <div className="mt-2.5">
                  <div className="mb-1 text-xs text-ink-subtle">Justificatif (ticket / facture)</div>
                  {l.justificatifNom
                    ? <div className="rounded-r-lg border border-success/50 bg-success/10 px-3 py-2.5 text-xs text-success">✓ {l.justificatifNom}{l.compressInfo ? ` — ${l.compressInfo}` : ''}{!lectureSeule && <label className="ml-2 cursor-pointer underline">· Remplacer<input type="file" accept=".pdf,image/*" hidden onChange={(e) => e.target.files?.[0] && choisirFichier(l.key, e.target.files[0])} /></label>}</div>
                    : <label className={`block rounded-r-lg border border-dashed border-border-hi px-3 py-3.5 text-center text-xs text-ink-subtle ${lectureSeule ? '' : 'cursor-pointer hover:border-border'}`}><Paperclip className="mr-1 inline h-3.5 w-3.5" />Glisser un ticket ou une facture, ou cliquer pour choisir (PDF, JPG, PNG){!lectureSeule && <input type="file" accept=".pdf,image/*" hidden onChange={(e) => e.target.files?.[0] && choisirFichier(l.key, e.target.files[0])} />}</label>}
                  <p className="mt-1 text-[11px] text-ink-subtle">Photo compressée automatiquement avant l'envoi pour limiter le stockage et accélérer l'upload mobile.</p>
                </div>
              )}

              {depasse && <p className="mt-2 text-[11px]" style={{ color: 'oklch(var(--c-warn))' }}>⚠ Dépassement du plafond conseillé ({eur(t!.plafond!)}). La note reste envoyable ; le dépassement sera signalé aux valideurs.</p>}
            </div>
          );
        })}

        {!lectureSeule && <Button variant="secondary" size="sm" onClick={ajouterLigne}><Plus className="h-4 w-4" /> Ajouter une dépense</Button>}
      </Card>

      <Card className="p-0">
        <div className="flex items-center justify-between px-4 py-3.5">
          <div><div className="text-xs text-ink-subtle">Total de la note ({lignes.length} dépense{lignes.length > 1 ? 's' : ''})</div><div className="text-2xl font-bold text-ink">{eur(total)}</div></div>
          {!lectureSeule && (
            <div className="flex gap-2">
              <Button variant="ghost" loading={saving} onClick={() => void enregistrer(false)}>Enregistrer en brouillon</Button>
              <Button variant="rh" loading={saving} onClick={() => void enregistrer(true)}>Envoyer pour validation →</Button>
            </div>
          )}
        </div>
      </Card>
    </div>
  );
}
